import * as React from 'react';
import {Helmet} from 'react-helmet';
import {Switch, Route} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {headTitle} from 'shells/document/head-title';

import {analyticsClient} from '../../../analytics-client';

import {signupAnalyticsRecord, checkProvisionStatus} from '../utils';

import {
    PROVISIONED_MESSAGE,
    STEPS_PATHNAMES,
    STEP_COMPANY,
    STEP_FEATURES,
    STEP_TEAM,
    STEP_CRM_EXPERIENCE,
    STEP_COMPLETE,
} from './consts';
import {convertDataToFormValues, generateDynamicPills} from './helpers';
import {GetToKnow} from './steps/get-to-know';
import {TeamSize} from './steps/team-size';
import {TrialGatheringPreparingPage} from './steps/trial-gathering-preparing-page';
import {WhatBringsYou} from './steps/what-brings-you';
import {WhereIsYourData} from './steps/where-is-your-data';
import type {AccountProvisionedState, ProvisionCheckResponse} from './types';

const INITIAL_PROVISION_STATE = {
    status: false,
    instanceId: null,
    emailSha1: null,
} as const;

const EMAIL_DOMAINS_TO_IGNORE = ['gmail.com'];

type Props = ContextRouter;

export function GatheringPage(props: Props) {
    const [currentStepIndex, setCurrentStepIndex] = React.useState<number>(0);
    const [companyName, setCompanyName] = React.useState<string>(
        props.location.state && props.location.state.companyName
            ? props.location.state.companyName
            : ''
    );
    const [logoUrl, setLogoUrl] = React.useState<string | null | undefined>(
        props.location.state && props.location.state.logoUrl ? props.location.state.logoUrl : ''
    );
    const [website, setWebsite] = React.useState<string>('');
    const [workPhone, setWorkPhone] = React.useState<string>('');
    const [textMessageOptIn, setTextMessageOptIn] = React.useState<boolean>(false);
    const [whatBringsYou, setWhatBringsYou] = React.useState<string[]>([]);
    const [teamSize, setTeamSize] = React.useState<string | null | undefined>(null);
    const [whereIsYourData, setWhereIsYourData] = React.useState<string[]>([]);
    // Store email and signupToken from initial location state so they persist between steps routes changes
    const emailRef = React.useRef<string>(
        props.location.state && props.location.state.email ? props.location.state.email : ''
    );
    const signupTokenRef = React.useRef<string>(
        props.location.state && props.location.state.signupToken
            ? props.location.state.signupToken
            : ''
    );

    const [accountProvisionedState, setAccountProvisionedState] =
        React.useState<AccountProvisionedState>(INITIAL_PROVISION_STATE);
    const [provisionErrorMessage, setProvisionErrorMessage] = React.useState<
        string | null | undefined
    >(null);

    // Track if initial check has been performed to avoid re-running on route changes when moving between steps
    const initialCheckDone = React.useRef<boolean>(false);

    React.useEffect(() => {
        if (initialCheckDone.current) {
            return;
        }
        initialCheckDone.current = true;

        if (!emailRef.current || !signupTokenRef.current) {
            props.history.push('/signup');

            return;
        }

        // Prefill website from email domain (e.g., person@domainName.com -> domainName.com)
        const emailDomain = emailRef.current.split('@')[1];
        if (emailDomain && !EMAIL_DOMAINS_TO_IGNORE.includes(emailDomain)) {
            setWebsite(emailDomain);
        }

        checkProvisionStatus(
            {username: emailRef.current, signupToken: signupTokenRef.current},
            (response: ProvisionCheckResponse) => {
                if (response && response.status === PROVISIONED_MESSAGE) {
                    // save instance id and sha1 email for use in nut analytics
                    setAccountProvisionedState({
                        status: true,
                        instanceId: response.instanceId,
                        emailSha1: response.emailSha1,
                    });
                }
            },
            () => {
                const message = 'This is awkward… something went wrong creating your account.';
                setProvisionErrorMessage(message);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNext = () => {
        props.history.push(`/signup/${STEPS_PATHNAMES[currentStepIndex + 1]}`);
        setCurrentStepIndex(currentStepIndex + 1);
    };

    const handleBack = () => {
        props.history.push(`/signup/${STEPS_PATHNAMES[currentStepIndex - 1]}`);
        setCurrentStepIndex(currentStepIndex - 1);
    };

    const handleStepViewed = (): void => {
        if (emailRef.current && signupTokenRef.current) {
            signupAnalyticsRecord({
                username: emailRef.current,
                signupToken: signupTokenRef.current,
                event: analyticsClient.EVENTS.TRACK_TINYINTRO_SLIDE_VIEWED,
                properties: {slide: `step-${currentStepIndex + 1}`},
            });
        }
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>{headTitle('Welcome')}</title>
            </Helmet>
            <div className='viewport-height'>
                <Switch>
                    <Route
                        path={`/signup/${STEP_COMPANY}`}
                        render={() => (
                            <GetToKnow
                                key='step-1'
                                onNext={handleNext}
                                fields={{
                                    companyName,
                                    setCompanyName,
                                    logoUrl,
                                    setLogoUrl,
                                    website,
                                    setWebsite,
                                    workPhone,
                                    setWorkPhone,
                                    textMessageOptIn,
                                    setTextMessageOptIn,
                                }}
                                handleStepViewed={handleStepViewed}
                            />
                        )}
                    />
                    <Route
                        path={`/signup/${STEP_FEATURES}`}
                        render={() => (
                            <WhatBringsYou
                                key='step-2'
                                onSkip={handleNext}
                                onBack={handleBack}
                                onNext={handleNext}
                                whatBringsYou={whatBringsYou}
                                setWhatBringsYou={setWhatBringsYou}
                                handleStepViewed={handleStepViewed}
                            />
                        )}
                    />
                    <Route
                        path={`/signup/${STEP_TEAM}`}
                        render={() => (
                            <TeamSize
                                key='step-3'
                                onBack={handleBack}
                                onNext={handleNext}
                                teamSize={teamSize}
                                setTeamSize={setTeamSize}
                                handleStepViewed={handleStepViewed}
                            />
                        )}
                    />
                    <Route
                        path={`/signup/${STEP_CRM_EXPERIENCE}`}
                        render={() => (
                            <WhereIsYourData
                                key='step-4'
                                onSkip={handleNext}
                                onBack={handleBack}
                                onNext={handleNext}
                                whereIsYourData={whereIsYourData}
                                setWhereIsYourData={setWhereIsYourData}
                                handleStepViewed={handleStepViewed}
                            />
                        )}
                    />
                    {/* teamSize will never be null at this point but need to allow it for the initial state */}
                    <Route
                        path={`/signup/${STEP_COMPLETE}`}
                        render={() => (
                            <TrialGatheringPreparingPage
                                key='step-5'
                                pills={generateDynamicPills(
                                    teamSize || 'no',
                                    website,
                                    whatBringsYou,
                                    whereIsYourData
                                )}
                                isProvisioningComplete={accountProvisionedState.status}
                                email={emailRef.current}
                                signupToken={signupTokenRef.current}
                                formValues={convertDataToFormValues(
                                    companyName,
                                    website,
                                    workPhone,
                                    textMessageOptIn,
                                    whatBringsYou,
                                    teamSize || 'no',
                                    whereIsYourData
                                )}
                                instanceId={accountProvisionedState.instanceId || ''}
                                emailSha1={accountProvisionedState.emailSha1 || ''}
                                errorProvisioning={provisionErrorMessage !== null}
                            />
                        )}
                    />
                </Switch>
            </div>
        </React.Fragment>
    );
}
